import type { RecurringItem, RecurringKind } from '@/api/recurring'
import { DEFAULT_BILL_ICON } from './billIconOptions'
import { DEFAULT_SUBSCRIPTION_ICON } from './subscriptionIconOptions'
import { fromPaise, toPaise } from './splitMath'

export const FREQUENCY_LABELS: Record<string, string> = {
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  HALF_YEARLY: 'Half-yearly',
  YEARLY: 'Yearly',
}

/** Calendar days from today to dateStr — negative if already past. Ignores time of day
 *  on both sides, so "today" is always 0 regardless of when this runs. */
export function daysUntil(dateStr: string): number {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const due = new Date(dateStr)
  due.setHours(0, 0, 0, 0)
  return Math.round((due.getTime() - today.getTime()) / 86400000)
}

export const KIND_LABELS: Record<RecurringKind, string> = {
  BILL: 'Bill',
  EMI: 'EMI',
  SUBSCRIPTION: 'Subscription',
  OTHER: 'Other',
}

// Payments-tab section order, one per kind.
export const KIND_SECTIONS: { kind: RecurringKind; title: string }[] = [
  { kind: 'BILL', title: 'Bills' },
  { kind: 'EMI', title: 'EMIs' },
  { kind: 'SUBSCRIPTION', title: 'Subscriptions' },
  { kind: 'OTHER', title: 'Other' },
]

const DEFAULT_ICON_BY_KIND: Record<RecurringKind, string> = {
  BILL: DEFAULT_BILL_ICON,
  EMI: 'landmark',
  SUBSCRIPTION: DEFAULT_SUBSCRIPTION_ICON,
  OTHER: 'receipt',
}

/** The glyph for a recurring item with no icon of its own — the linked category's icon
 *  if there is one, else a generic default for its kind (or a plain default for INCOME,
 *  which has no kind). Never leaves a row blank. */
export function getDefaultRecurringIcon(
  kind: RecurringKind | null,
  categoryIcon?: string | null
): string {
  if (categoryIcon) return categoryIcon
  if (kind) return DEFAULT_ICON_BY_KIND[kind]
  return 'circle-dollar-sign'
}

export type ShareSummary =
  | { kind: 'chipIn'; yours: number; othersTotal: number; others: string[]; archived: string[] }
  | { kind: 'paidBy'; payer: string; archived: string[] }

/** How a shared recurring payment divides up — null when it's the household's alone. */
export function shareSummary(
  item: Pick<RecurringItem, 'amount' | 'splits' | 'paidByContactName' | 'paidByContactArchived'>
): ShareSummary | null {
  if (item.paidByContactName) {
    return {
      kind: 'paidBy',
      payer: item.paidByContactName,
      archived: item.paidByContactArchived ? [item.paidByContactName] : [],
    }
  }
  const splits = item.splits ?? []
  if (splits.length === 0) return null
  const othersPaise = splits.reduce((s, x) => s + toPaise(x.amount), 0)
  return {
    kind: 'chipIn',
    yours: fromPaise(toPaise(item.amount) - othersPaise),
    othersTotal: fromPaise(othersPaise),
    others: splits.map((s) => s.contactName ?? 'Someone'),
    archived: splits.filter((s) => s.contactArchived).map((s) => s.contactName ?? 'Someone'),
  }
}

/** An estimated amount reads "~₹2,500"; a fixed one as it is. */
export function approxAmount(item: { amountVaries?: boolean }, formatted: string): string {
  return item.amountVaries ? `~${formatted}` : formatted
}
